import { useEffect, useState } from 'react'
import {
  createFileRoute,
  Outlet,
  redirect,
  useMatchRoute,
  useNavigate,
} from '@tanstack/react-router'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { hasTokens } from '../lib/auth'
import { useClients } from '../hooks/useClients'
import { useZones } from '../hooks/useZones'
import { useMe } from '../hooks/useAuth'
import { useUsersList } from '../hooks/useComments'
import { bulkAssignClients, bulkSetPriorita, bulkArchiveClients, exportClientsCsv } from '../lib/api'
import { BulkSendMessageModal } from '../components/cliente/BulkSendMessageModal'
import { canWriteClient, isFullView } from '../lib/permissions'
import { CardSkeleton } from '../components/Skeleton'
import { EmptyState } from '../components/EmptyState'
import { ClientFormModal } from '../components/ClientFormModal'
import { SavedViewsDropdown } from '../components/SavedViewsDropdown'
import type { ClientRow } from '../lib/api'

type ClientiSearch = {
  page: number
  page_size: number
  q: string
  consultant_id?: string
  priorita?: string
  status?: string
  rating?: string
  zona_tag?: string
  sort: string
  filter?: string
}

export const Route = createFileRoute('/clienti')({
  beforeLoad: () => {
    if (!hasTokens()) {
      throw redirect({ to: '/login' })
    }
  },
  validateSearch: (search: Record<string, unknown>): ClientiSearch => {
    const toNum = (v: unknown, dflt: number): number => {
      const n = typeof v === 'number' ? v : Number(v)
      return Number.isFinite(n) && n > 0 ? n : dflt
    }
    const toStr = (v: unknown): string => (typeof v === 'string' ? v : '')
    const toOptStr = (v: unknown): string | undefined =>
      typeof v === 'string' && v.length > 0 ? v : undefined
    return {
      page: toNum(search.page, 1),
      page_size: toNum(search.page_size, 50),
      q: toStr(search.q),
      consultant_id: toOptStr(search.consultant_id),
      priorita: toOptStr(search.priorita),
      status: toOptStr(search.status),
      rating: toOptStr(search.rating),
      zona_tag: toOptStr(search.zona_tag),
      sort: toStr(search.sort) || '-created_at',
      filter: toOptStr(search.filter),
    }
  },
  component: ClientiPage,
})

const PRIO_CLASS: Record<string, string> = {
  ATTIVO: 'attivo',
  MEDIO: 'medio',
  PASSIVO: 'passivo',
  NI: 'ni',
}

const PRIO_LABEL: Record<string, string> = {
  ATTIVO: 'Attivo',
  MEDIO: 'Medio',
  PASSIVO: 'Passivo',
  NI: 'N.I.',
}

function fmtCurrency0(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—'
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
    notation: v >= 1000 ? 'compact' : 'standard',
  }).format(v)
}

function shortCode(code: number | null | undefined): string {
  if (code === null || code === undefined) return ''
  return `C_${code.toString().padStart(5, '0')}`
}

function avatarHue(name: string): number {
  let h = 0
  for (const c of name) h = (h * 31 + c.charCodeAt(0)) % 360
  return h
}

function shortInitials(name: string | null | undefined): string {
  if (!name) return '?'
  const parts = name.trim().split(/\s+/)
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + (parts[parts.length - 1][0] ?? '')).toUpperCase()
}

function ClientiPage() {
  const matchRoute = useMatchRoute()
  const onChild =
    matchRoute({ to: '/clienti/$id', fuzzy: true }) ||
    matchRoute({ to: '/clienti/$id/match' })
  if (onChild) return <Outlet />
  return <ClientiList />
}

function ClientiList() {
  const search = Route.useSearch()
  const navigate = useNavigate({ from: '/clienti' })
  const { data: me } = useMe()
  const canCreate = canWriteClient(me)

  const [qLocal, setQLocal] = useState(search.q)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [createOpen, setCreateOpen] = useState(false)
  const [density, setDensity] = useState<'comoda' | 'densa'>('comoda')

  useEffect(() => setQLocal(search.q), [search.q])
  useEffect(() => {
    const t = setTimeout(() => {
      if (qLocal !== search.q) {
        void navigate({ search: { ...search, q: qLocal, page: 1 }, replace: true })
      }
    }, 600)
    return () => clearTimeout(t)
  }, [qLocal, search, navigate])

  const zonesQuery = useZones()
  const zonaOptions = [
    { value: '', label: 'Tutte le zone' },
    { value: '__none__', label: '(Senza zona)' },
    ...(zonesQuery.data?.items ?? []).map((z) => ({
      value: z.tag,
      label: z.tag,
    })),
  ]

  const { data, isLoading, isError } = useClients({
    page: search.page,
    page_size: search.page_size,
    q: search.q || undefined,
    consultant_id: search.consultant_id,
    priorita: search.priorita,
    rating: search.rating,
    status: search.status,
    backlog_pending: search.filter === 'backlog-pending' ? true : undefined,
    zona_tag: search.zona_tag,
    sort: search.sort,
  })

  const items = data?.items ?? []
  const total = data?.total ?? 0
  const pages = data?.pages ?? 1

  const queryClient = useQueryClient()
  const usersQuery = useUsersList()
  const consultants = (usersQuery.data?.items ?? []).filter(
    (u) => u.role === 'consulente',
  )
  const canAssign = isFullView(me)
  // Esporta è gated come Assegna: solo full-view (Michele/Giulia).
  const canExport = canAssign
  const bulkAssign = useMutation({
    mutationFn: (consultantId: string) =>
      bulkAssignClients([...selected], consultantId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['clients'] })
      setSelected(new Set())
    },
  })

  const [bulkMsgOpen, setBulkMsgOpen] = useState(false)

  const bulkPriorita = useMutation({
    mutationFn: (priorita: string) => bulkSetPriorita([...selected], priorita),
    onSuccess: (res) => {
      void queryClient.invalidateQueries({ queryKey: ['clients'] })
      setSelected(new Set())
      if (res.updated === 0) {
        window.alert(
          'Nessun cliente aggiornato. Come consulente puoi cambiare la priorità solo dei clienti di cui sei responsabile.',
        )
      }
    },
    onError: (err) => {
      window.alert(`Cambio priorità fallito: ${(err as Error).message}`)
    },
  })

  const bulkArchive = useMutation({
    mutationFn: () => bulkArchiveClients([...selected]),
    onSuccess: (res) => {
      void queryClient.invalidateQueries({ queryKey: ['clients'] })
      setSelected(new Set())
      if (res.updated === 0) {
        window.alert(
          'Nessun cliente archiviato. Come consulente puoi archiviare solo i clienti di cui sei responsabile.',
        )
      }
    },
    onError: (err) => {
      window.alert(`Archiviazione fallita: ${(err as Error).message}`)
    },
  })

  const bulkExport = useMutation({
    mutationFn: () => exportClientsCsv([...selected]),
    onSuccess: (blob) => {
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'clienti_export.csv'
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    },
    onError: (err) => {
      window.alert(`Export fallito: ${(err as Error).message}`)
    },
  })

  const activeView =
    search.status === 'archiviato' ? 'archiviato' : (search.priorita ?? 'all')
  const filterCount = [
    search.q,
    search.consultant_id,
    search.priorita,
    search.rating,
    search.zona_tag,
  ].filter((v) => v !== undefined && v !== '').length

  function setView(view: { priorita?: string; status?: string }) {
    // Ogni tab azzera l'altro filtro: priorita e status sono mutuamente
    // esclusivi (undefined => param rimosso dall'URL dal router).
    void navigate({
      search: {
        ...search,
        priorita: view.priorita ?? undefined,
        status: view.status ?? undefined,
        page: 1,
      },
      replace: true,
    })
    setSelected(new Set())
  }

  function setSort(s: string) {
    void navigate({ search: { ...search, sort: s, page: 1 }, replace: true })
  }

  function goToPage(p: number) {
    void navigate({ search: { ...search, page: p }, replace: true })
  }

  function openClient(id: string) {
    void navigate({ to: '/clienti/$id', params: { id } } as never)
  }

  function toggleSelect(id: string) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function toggleSelectAll() {
    setSelected((prev) =>
      prev.size === items.length ? new Set() : new Set(items.map((c) => c.id)),
    )
  }

  function resetFilters() {
    void navigate({
      search: { page: 1, page_size: 50, q: '', sort: '-created_at' },
      replace: true,
    })
  }

  if (isLoading) return <CardSkeleton />
  if (isError) {
    return (
      <EmptyState
        icon="⚠️"
        title="Errore caricamento clienti"
        body="Riprova fra qualche secondo."
      />
    )
  }

  const selectedCount = selected.size

  return (
    <>
      {/* BACKLOG FILTER BANNER */}
      {search.filter === 'backlog-pending' && (
        <div
          style={{
            padding: 'var(--sp-2) var(--sp-3)',
            background: 'var(--accent-soft)',
            color: 'var(--accent-ink)',
            borderRadius: 'var(--r-md)',
            marginBottom: 'var(--sp-3)',
            fontSize: 'var(--fs-12)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span>
            🔔 Filtro <b>backlog pending</b> attivo
            {search.consultant_id && ' (consulente specifico)'}
          </span>
          <button
            type="button"
            className="btn sm ghost"
            onClick={() =>
              void navigate({
                search: (old) => ({
                  ...old,
                  filter: undefined,
                  consultant_id: undefined,
                }),
              })
            }
          >
            ✕ Rimuovi filtro
          </button>
        </div>
      )}

      {/* PAGE HEADER */}
      <div className="page-header">
        <div>
          <div className="breadcrumb">
            Operativo · <span className="here">Clienti</span>
          </div>
          <div className="page-title-row">
            <h1>Clienti</h1>
            <span className="total">
              <b>{total.toLocaleString('it-IT')}</b> totali
            </span>
          </div>
        </div>
        <div className="page-actions">
          <button type="button" className="btn">
            <SvgDownload />
            Esporta CSV
          </button>
          {canCreate && (
            <button
              type="button"
              className="btn primary"
              onClick={() => setCreateOpen(true)}
            >
              <SvgPlus />
              Nuovo cliente
            </button>
          )}
        </div>
      </div>

      {/* SAVED VIEWS */}
      <div className="savedviews">
        <a
          className={`sv-tab${activeView === 'all' ? ' active' : ''}`}
          onClick={() => setView({})}
        >
          Tutti <span className="ct">{total}</span>
        </a>
        <a
          className={`sv-tab${activeView === 'ATTIVO' ? ' active' : ''}`}
          onClick={() => setView({ priorita: 'ATTIVO' })}
        >
          Attivi
        </a>
        <a
          className={`sv-tab${activeView === 'MEDIO' ? ' active' : ''}`}
          onClick={() => setView({ priorita: 'MEDIO' })}
        >
          In valutazione
        </a>
        <a
          className={`sv-tab${activeView === 'PASSIVO' ? ' active' : ''}`}
          onClick={() => setView({ priorita: 'PASSIVO' })}
        >
          Inattivi
        </a>
        <a
          className={`sv-tab${activeView === 'NI' ? ' active' : ''}`}
          onClick={() => setView({ priorita: 'NI' })}
        >
          N.I.
        </a>
        <a
          className={`sv-tab${activeView === 'archiviato' ? ' active' : ''}`}
          onClick={() => setView({ status: 'archiviato' })}
        >
          Archiviati
        </a>
        <span className="sv-divider" />
        <a
          className={`sv-tab${search.filter === 'backlog-pending' ? ' active' : ''}`}
          title="Mostra solo clienti con proposte di lotti ancora da segnalare"
          onClick={() =>
            navigate({
              search: {
                ...search,
                filter:
                  search.filter === 'backlog-pending'
                    ? undefined
                    : 'backlog-pending',
                page: 1,
              },
              replace: true,
            })
          }
        >
          🔔 Da segnalare
          {search.filter === 'backlog-pending' && (
            <span className="ct">{total}</span>
          )}
        </a>
        <span className="sv-divider" />
        <SavedViewsDropdown
          entityType="clienti"
          currentFilters={search}
          onApply={(filters) =>
            navigate({
              search: { ...search, ...filters, page: 1 },
              replace: true,
            })
          }
          label="+ Salva vista"
        />
      </div>

      {/* FILTER BAR */}
      <div className="filterbar">
        <div className="filterbar-row">
          <div className="fb-search">
            <span className="ico">
              <SvgSearch />
            </span>
            <input
              type="text"
              value={qLocal}
              onChange={(e) => setQLocal(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && qLocal !== search.q) {
                  void navigate({ search: { ...search, q: qLocal, page: 1 }, replace: true })
                }
              }}
              placeholder="Cerca per codice C_, nome, email, telefono…"
              aria-label="Ricerca clienti"
            />
          </div>

          <FilterChipSelect
            label="Priorità"
            value={search.priorita}
            displayValue={search.priorita ? PRIO_LABEL[search.priorita] : undefined}
            options={[
              { value: '', label: 'Tutti' },
              { value: 'ATTIVO', label: 'Attivo' },
              { value: 'MEDIO', label: 'Medio' },
              { value: 'PASSIVO', label: 'Passivo' },
              { value: 'NI', label: 'N.I.' },
            ]}
            onChange={(v) =>
              navigate({
                search: { ...search, priorita: v || undefined, page: 1 },
                replace: true,
              })
            }
          />
          <FilterChipSelect
            label="Rating"
            value={search.rating}
            displayValue={search.rating}
            options={[
              { value: '', label: 'Tutti' },
              { value: 'A', label: 'A' },
              { value: 'B', label: 'B' },
              { value: 'C', label: 'C' },
            ]}
            onChange={(v) =>
              navigate({
                search: { ...search, rating: v || undefined, page: 1 },
                replace: true,
              })
            }
          />
          <FilterChipSelect
            label="Zona"
            value={search.zona_tag}
            displayValue={
              search.zona_tag === '__none__' ? '(Senza zona)' : search.zona_tag
            }
            options={zonaOptions}
            onChange={(v) =>
              navigate({
                search: { ...search, zona_tag: v || undefined, page: 1 },
                replace: true,
              })
            }
          />

          {filterCount > 0 && (
            <>
              <span className="fb-divider" />
              <button
                type="button"
                className="fb-clear"
                onClick={resetFilters}
              >
                Reset filtri ({filterCount})
              </button>
            </>
          )}
        </div>
        <div className="fb-meta">
          <span>
            <span className="strong">{total.toLocaleString('it-IT')}</span>{' '}
            clienti totali · <span className="strong">{items.length}</span>{' '}
            visibili in pagina
          </span>
          <div className="right">
            <span>Densità:</span>
            <div className="segmented" style={{ border: 0, padding: 0 }}>
              <button
                type="button"
                className={density === 'comoda' ? 'on' : ''}
                onClick={() => setDensity('comoda')}
              >
                Comoda
              </button>
              <button
                type="button"
                className={density === 'densa' ? 'on' : ''}
                onClick={() => setDensity('densa')}
              >
                Densa
              </button>
            </div>
            <span className="fb-divider" style={{ width: 1, height: 14 }} />
            <span>
              Ordinato per{' '}
              <select
                value={search.sort}
                onChange={(e) => setSort(e.target.value)}
                style={{
                  border: 0,
                  background: 'transparent',
                  fontWeight: 'var(--fw-medium)',
                  color: 'var(--ink-1)',
                  cursor: 'pointer',
                }}
              >
                <option value="-created_at">Recenti</option>
                <option value="created_at">Vecchi</option>
                <option value="cognome">Cognome A→Z</option>
                <option value="-cognome">Cognome Z→A</option>
                <option value="-budget">Budget ↓</option>
                <option value="budget">Budget ↑</option>
                <option value="-completeness">Completezza ↓</option>
                <option value="completeness">Completezza ↑</option>
              </select>
            </span>
          </div>
        </div>
      </div>

      {/* BULK BAR */}
      {selectedCount > 0 && (
        <div className="bulkbar">
          <span className="selcount">{selectedCount} selezionati</span>
          {canAssign && (
            <select
              className="bba"
              value=""
              disabled={bulkAssign.isPending}
              onChange={(e) => {
                if (e.target.value) bulkAssign.mutate(e.target.value)
              }}
              aria-label="Assegna consulente ai selezionati"
            >
              <option value="">
                {bulkAssign.isPending ? 'Assegnazione…' : 'Assegna consulente…'}
              </option>
              {consultants.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.display_name}
                </option>
              ))}
            </select>
          )}
          <select
            className="bba"
            value=""
            disabled={bulkPriorita.isPending}
            onChange={(e) => {
              if (e.target.value) bulkPriorita.mutate(e.target.value)
            }}
            aria-label="Sposta priorità dei selezionati"
          >
            <option value="">
              {bulkPriorita.isPending ? 'Aggiornamento…' : 'Sposta priorità…'}
            </option>
            {Object.entries(PRIO_LABEL).map(([code, label]) => (
              <option key={code} value={code}>
                {label}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="bba"
            onClick={() => setBulkMsgOpen(true)}
          >
            Invia messaggio
          </button>
          {canExport && (
            <button
              type="button"
              className="bba"
              disabled={bulkExport.isPending}
              onClick={() => bulkExport.mutate()}
            >
              {bulkExport.isPending ? 'Esportazione…' : 'Esporta selezione'}
            </button>
          )}
          <button
            type="button"
            className="bba danger"
            disabled={bulkArchive.isPending}
            onClick={() => {
              if (
                window.confirm(
                  `Archiviare ${selectedCount} clienti? Spariranno dalla lista (visibili nella tab Archiviati).`,
                )
              ) {
                bulkArchive.mutate()
              }
            }}
          >
            {bulkArchive.isPending ? 'Archiviazione…' : 'Archivia'}
          </button>
          <span className="spacer" />
          <button
            type="button"
            className="deselect"
            onClick={() => setSelected(new Set())}
            style={{ background: 'transparent', border: 0, cursor: 'pointer' }}
          >
            Deseleziona tutti
          </button>
        </div>
      )}

      {/* TABLE */}
      {items.length === 0 ? (
        <EmptyState
          icon="👤"
          title="Nessun cliente matcha i filtri"
          body="Modifica filtri o resetta per vedere tutto il portfolio."
          action={{ label: 'Reset filtri', onClick: resetFilters }}
        />
      ) : (
        <div className={`table-card${density === 'densa' ? ' dense' : ''}`}>
          <table className="tbl">
            <thead>
              <tr>
                <th className="check-col">
                  <span
                    className={`cbox${
                      selectedCount === items.length
                        ? ' checked'
                        : selectedCount > 0
                        ? ' indeterminate'
                        : ''
                    }`}
                    onClick={toggleSelectAll}
                    style={{ cursor: 'pointer' }}
                  />
                </th>
                <th className="sort">Codice</th>
                <th className="sort">Cliente</th>
                <th>Contatto</th>
                <th className="sort">Priorità</th>
                <th className="sort">Rating</th>
                <th className="sort">Budget</th>
                <th>Zone interesse</th>
                <th>Tipologia</th>
                {isFullView(me) && <th>Consulente</th>}
                <th className="sort">Completezza</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <ClientRowEl
                  key={c.id}
                  c={c}
                  selected={selected.has(c.id)}
                  showConsultant={isFullView(me)}
                  onToggle={() => toggleSelect(c.id)}
                  onOpen={() => openClient(c.id)}
                />
              ))}
            </tbody>
          </table>

          <div className="pagination">
            <div className="pgleft">
              <span>Pagina</span>
              <span>
                · <b className="mono" style={{ color: 'var(--ink-1)' }}>
                  {(search.page - 1) * search.page_size + 1}–
                  {(search.page - 1) * search.page_size + items.length}
                </b>{' '}
                di{' '}
                <b className="mono" style={{ color: 'var(--ink-1)' }}>
                  {total.toLocaleString('it-IT')}
                </b>
              </span>
            </div>
            <div className="pgnav">
              <button
                type="button"
                className={search.page <= 1 ? 'disabled' : ''}
                disabled={search.page <= 1}
                onClick={() => goToPage(search.page - 1)}
              >
                ←
              </button>
              <span className="muted mono" style={{ padding: '0 var(--sp-2)' }}>
                {search.page} / {pages}
              </span>
              <button
                type="button"
                className={search.page >= pages ? 'disabled' : ''}
                disabled={search.page >= pages}
                onClick={() => goToPage(search.page + 1)}
              >
                →
              </button>
            </div>
          </div>
        </div>
      )}

      {createOpen && (
        <ClientFormModal
          mode="create"
          onClose={() => setCreateOpen(false)}
          onSuccess={() => setCreateOpen(false)}
        />
      )}
      {bulkMsgOpen && (
        <BulkSendMessageModal
          clientIds={[...selected]}
          onClose={() => setBulkMsgOpen(false)}
          onSent={() => {
            void queryClient.invalidateQueries({ queryKey: ['clients'] })
            setSelected(new Set())
          }}
        />
      )}
    </>
  )
}

function FilterChipSelect({
  label,
  value,
  displayValue,
  options,
  onChange,
}: {
  label: string
  value: string | undefined
  displayValue?: string
  options: Array<{ value: string; label: string }>
  onChange: (v: string) => void
}) {
  const hasValue = Boolean(value)
  return (
    <label
      className={`fb-chip${hasValue ? ' has-value' : ''}`}
      style={{ position: 'relative', cursor: 'pointer' }}
    >
      <span className="label">{label}</span>
      {hasValue && displayValue && <span className="val">{displayValue}</span>}
      <span className="caret">
        <SvgCaret />
      </span>
      <select
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          opacity: 0,
          cursor: 'pointer',
          border: 0,
          background: 'transparent',
          appearance: 'none',
        }}
        aria-label={label}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  )
}

function ClientRowEl({
  c,
  selected,
  showConsultant,
  onToggle,
  onOpen,
}: {
  c: ClientRow
  selected: boolean
  showConsultant: boolean
  onToggle: () => void
  onOpen: () => void
}) {
  const fullName = `${c.cognome ?? ''} ${c.nome ?? ''}`.trim() || '—'
  const hue = avatarHue(fullName)
  const prioCls = PRIO_CLASS[c.priorita] ?? 'ni'
  const prioLabel = PRIO_LABEL[c.priorita] ?? c.priorita
  const ratingCls = c.rating ? c.rating.toLowerCase() : 'empty'
  const ratingLabel = c.rating ?? '—'
  const budgetLabel = c.budget != null ? fmtCurrency0(c.budget) : '—'
  const consName = c.consultant_name ?? null
  const consHue = consName ? avatarHue(consName) : null
  const complPct = typeof c.completeness_pct === 'number' ? c.completeness_pct : null
  const complColor =
    complPct == null
      ? ''
      : complPct >= 80
      ? 'var(--ok-ink)'
      : complPct >= 50
      ? 'var(--warn-ink)'
      : 'var(--risk-ink)'
  return (
    <tr className={selected ? 'selected' : undefined}>
      <td className="check-col">
        <span
          className={`cbox${selected ? ' checked' : ''}`}
          onClick={onToggle}
          style={{ cursor: 'pointer' }}
        />
      </td>
      <td onClick={onOpen} style={{ cursor: 'pointer' }}>
        <span className="tag code">{shortCode(c.short_code)}</span>
      </td>
      <td onClick={onOpen} style={{ cursor: 'pointer' }}>
        <div className="cli-cell">
          <div className="avatar lg" style={{ background: `oklch(58% 0.12 ${hue})` }}>
            {shortInitials(fullName)}
          </div>
          <div className="info">
            <div className="nm">{fullName}</div>
            {c.email && <div className="em">{c.email}</div>}
          </div>
        </div>
      </td>
      <td>
        <div className="contact-cell">
          {c.telefono && <span className="tel">{c.telefono}</span>}
          <div className="links">
            {c.telefono && (
              <a
                className="wa"
                href={`https://wa.me/${c.telefono.replace(/\D/g, '')}`}
                target="_blank"
                rel="noreferrer"
                title="WhatsApp"
                onClick={(e) => e.stopPropagation()}
              >
                <SvgWhatsApp />
              </a>
            )}
            {c.email && (
              <a
                href={`mailto:${c.email}`}
                title="Email"
                onClick={(e) => e.stopPropagation()}
              >
                <SvgMail />
              </a>
            )}
            {c.telefono && (
              <a
                href={`tel:${c.telefono.replace(/\s+/g, '')}`}
                title="Chiama"
                onClick={(e) => e.stopPropagation()}
              >
                <SvgPhone />
              </a>
            )}
          </div>
        </div>
      </td>
      <td>
        <span className={`prio ${prioCls}`}>
          <span className="dot" />
          {prioLabel}
        </span>
      </td>
      <td>
        <span className={`rating ${ratingCls}`}>{ratingLabel}</span>
      </td>
      <td>
        <span className="budget-val">{budgetLabel}</span>
      </td>
      <td>
        {c.zone_tag && c.zone_tag.length > 0 ? (
          <span className="zones">
            <span className="head">{c.zone_tag[0]}</span>
            {c.zone_tag.length > 1 && <span className="more">+{c.zone_tag.length - 1}</span>}
          </span>
        ) : (
          <span className="muted">—</span>
        )}
      </td>
      <td>
        <div className="tipo-cell">
          {c.tipologia ? (
            <span className={`tag tipo-${c.tipologia.toLowerCase().slice(0, 5)}`}>
              {c.tipologia.toUpperCase()}
            </span>
          ) : (
            <span className="muted">—</span>
          )}
        </div>
      </td>
      {showConsultant && (
        <td>
          {consName ? (
            <div className="assignee">
              <div className="avatar xs" style={{ background: `oklch(58% 0.12 ${consHue ?? 0})` }}>
                {shortInitials(consName)}
              </div>
              <span>{consName}</span>
            </div>
          ) : (
            <span className="muted">Da assegnare</span>
          )}
        </td>
      )}
      <td>
        {complPct != null ? (
          <span
            style={{
              fontVariantNumeric: 'tabular-nums',
              color: complColor,
              fontWeight: 'var(--fw-semibold)',
            }}
          >
            {complPct}%
          </span>
        ) : (
          <span className="muted">—</span>
        )}
      </td>
      <td>
        <div className="row-actions">
          <button type="button" title="Apri scheda" onClick={onOpen}>
            <SvgOpenLink />
          </button>
        </div>
      </td>
    </tr>
  )
}

// ============== SVG icons inline ==================

function SvgSearch() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round">
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </svg>
  )
}

function SvgPlus() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round">
      <path d="M12 5v14M5 12h14" />
    </svg>
  )
}

function SvgDownload() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3v12" />
      <path d="m7 10 5 5 5-5" />
      <path d="M5 21h14" />
    </svg>
  )
}

function SvgCaret() {
  return (
    <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
      <path d="m6 9 6 6 6-6" />
    </svg>
  )
}

function SvgWhatsApp() {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2a10 10 0 0 0-8.5 15.2L2 22l4.9-1.4A10 10 0 1 0 12 2zm5 14c-.2.6-1.3 1.2-1.8 1.2-.5.1-1 .1-3.3-.7-2.8-1-4.6-3.8-4.7-4-.1-.2-1.1-1.4-1.1-2.7s.7-1.9 1-2.2c.2-.2.5-.2.7-.2h.5c.2 0 .4 0 .6.5l.8 2c.1.2.1.4 0 .5l-.3.5c-.1.2-.2.3-.4.5-.1.2-.3.3-.1.6.2.3 1 1.6 2 2.4 1.4 1.2 2.5 1.5 2.8 1.7.3.1.5.1.6-.1l.8-1c.1-.2.3-.2.5-.1l1.8.9c.2.1.4.2.5.3 0 .1 0 .8-.3 1.4z" />
    </svg>
  )
}

function SvgMail() {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="m3 7 9 7 9-7" />
    </svg>
  )
}

function SvgPhone() {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.91.7 2.82a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.82.7A2 2 0 0 1 22 16.92Z" />
    </svg>
  )
}

function SvgOpenLink() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
      <path d="M7 17 17 7M9 7h8v8" />
    </svg>
  )
}
